import { useState, useCallback, useMemo } from 'react'
import { SocialWebStoryScopeWidget as SocialWebStoryScopeWidgetType } from 'schemas/SocialWebStoryScopeWidgetSchema'
import { useEmojiNameToDisplayMap } from 'hooks/useEmojiState'
import useSearchFilterComposingStrategies from './useSearchFilterComposingStrategies'
import useWidgetSettingComposingStrategies from './useWidgetSettingComposingStrategies'
import { convertToInsightContentSetting, addSelectionFilterToWidgetSetting } from '../utils'
import { SelectedInsight } from '../types'

const useSelectedInsights = (widgetSetting: SocialWebStoryScopeWidgetType) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedInsights, setSelectedInsights] = useState<SelectedInsight[]>([])
  const emojiNameToDisplayMap = useEmojiNameToDisplayMap()

  const searchFilterComposingStrategies = useSearchFilterComposingStrategies(
    widgetSetting,
    selectedInsights
  )
  const widgetSettingComposingStrategies = useWidgetSettingComposingStrategies(
    widgetSetting,
    selectedInsights
  )

  const detailSetting = useMemo(() => {
    if (selectedInsights.length === 0) {
      return undefined
    }
    return convertToInsightContentSetting(
      widgetSetting,
      searchFilterComposingStrategies,
      {
        insights: selectedInsights
      },
      emojiNameToDisplayMap
    )
  }, [searchFilterComposingStrategies, widgetSetting, selectedInsights, emojiNameToDisplayMap])

  const widgetSettingWithSelection = useMemo(() => {
    if (selectedInsights.length === 0) {
      return undefined
    }
    return addSelectionFilterToWidgetSetting(widgetSetting, widgetSettingComposingStrategies, {
      insights: selectedInsights
    })
  }, [widgetSettingComposingStrategies, widgetSetting, selectedInsights])

  const onClick = useCallback(
    (insightDataArray: SelectedInsight[]) => {
      setIsModalOpen(true)
      setSelectedInsights(insightDataArray)
    },
    [setIsModalOpen, setSelectedInsights]
  )

  const onClose = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  return {
    isModalOpen,
    selectedInsights,
    onClick,
    onClose,
    detailSetting,
    widgetSettingWithSelection
  }
}

export default useSelectedInsights
